import { useState } from "react";
import { api } from "../../api/client";
import { ActionStatus, PayFeeSelect, usePayFeeIn } from "../../components/ActionStatus";
import { usePlayer } from "../../state/PlayerContext";
import { formatRcd, formatSol, solToLamports } from "../format";
import { useWalletAction } from "../useWalletAction";

/** Vault balance, refundable SOL locked in owned Pokémon, Add / Withdraw (§7.6 `vault`, §15.5.2). */
export function VaultPanel({ suggestedTopUp }: { suggestedTopUp?: string | null }) {
  const { me, refresh } = usePlayer();
  const add = useWalletAction();
  const withdraw = useWalletAction();
  const [addSol, setAddSol] = useState("");
  const [withdrawSol, setWithdrawSol] = useState("");
  const fee = usePayFeeIn();
  const payFeeIn = fee.value;
  const [inputError, setInputError] = useState<string | null>(null);

  if (!me) return null;
  const v = me.vault;
  const topUp = suggestedTopUp ?? (BigInt(v.topUpNeeded) > 0n ? v.topUpNeeded : null);

  const onAdd = async () => {
    setInputError(null);
    let lamports: string;
    try {
      lamports = addSol ? solToLamports(addSol) : (topUp ?? "");
      if (!lamports || BigInt(lamports) <= 0n) throw new Error("Enter an amount in SOL");
    } catch (e) {
      setInputError((e as Error).message);
      return;
    }
    if (await add.run(() => api.vaultDeposit(lamports))) {
      setAddSol("");
      void refresh();
    }
  };

  const onWithdraw = async () => {
    setInputError(null);
    let lamports: bigint;
    try {
      lamports = BigInt(solToLamports(withdrawSol));
    } catch (e) {
      setInputError((e as Error).message);
      return;
    }
    const after = BigInt(v.lamports) - lamports;
    if (after < 0n) return setInputError("That's more than the vault holds.");
    if (after > 0n && after < BigInt(v.minimum)) {
      return setInputError(`The vault must keep 0 or at least ${formatSol(v.minimum)} SOL. Withdraw everything or leave the minimum.`);
    }
    if (await withdraw.run(() => api.vaultWithdraw(lamports.toString(), payFeeIn))) {
      setWithdrawSol("");
      void refresh();
    }
  };

  return (
    <section className="panel" aria-labelledby="vault-h">
      <h3 id="vault-h">Vault</h3>
      <dl className="kv">
        <dt>Balance</dt>
        <dd>{formatSol(v.lamports)} SOL</dd>
        <dt>Refundable in your Pokémon</dt>
        <dd>{formatSol(v.refundableLockedLamports)} SOL</dd>
        {v.pendingMints > 0 && (
          <>
            <dt>Waiting to mint</dt>
            <dd>
              {v.pendingMints} Pokémon
              {v.rentPoolShort ? " · pool refilling" : BigInt(v.mintRcdNeeded) > 0n ? ` · ${formatRcd(v.mintRcdNeeded)} more needed` : ""}
            </dd>
          </>
        )}
        {v.pendingItemBag && (
          <>
            <dt>Rare items</dt>
            <dd>waiting to record</dd>
          </>
        )}
      </dl>
      {topUp && <p className="banner warn">Add {formatSol(topUp)} SOL to record your waiting rare items.</p>}
      <p className="hint small">New Pokémon are minted from your in-game ₽; the vault only holds SOL for your rare-item bag.</p>
      <div className="row">
        <input inputMode="decimal" placeholder={topUp ? formatSol(topUp) : "SOL"} value={addSol} onChange={(e) => setAddSol(e.target.value)} aria-label="SOL to add" />
        <button disabled={add.busy} onClick={onAdd}>
          Add SOL
        </button>
      </div>
      <ActionStatus {...add} />
      <div className="row">
        <input inputMode="decimal" placeholder="SOL" value={withdrawSol} onChange={(e) => setWithdrawSol(e.target.value)} aria-label="SOL to withdraw" />
        <button disabled={withdraw.busy || !withdrawSol} onClick={onWithdraw}>
          Withdraw
        </button>
      </div>
      <PayFeeSelect {...fee} />
      {inputError && <p className="status error">{inputError}</p>}
      <ActionStatus {...withdraw} />
    </section>
  );
}
